import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useReducer
} from 'react';
import {
  AccountClient,
  savePersonalization,
  getPersonalization,
  getNonAuthToken,
  NonAuthAccountClient
} from '@nile/graphql';
import {
  withErrorBoundary,
  EventEmitter,
  AppContext,
  SkeletonLoading,
  getParsedCrudData,
  preFormatForm,
  validateForm,
  ReportConstraints,
  getTranslatedText,
  useActivityTracker,
  toast,
  Sheet,
  isAuthorised,
  useIsAuthorisedHook
} from '@nile/components';
import Loading from '@nile/components/src/Svg/AnimatedSvg/Loading.svg';
import { useLocation, withRouter } from 'react-router-dom';
import {
  GET_REPORTS,
  UPDATE_REPORT,
  CREATE_REPORT
} from '@nile/reports/src/modules/reports/graphql';
import { GET_CHART_VIEWS } from '@nile/reports/src/modules/charts/graphql';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import NavigationContainer from '@nile/reports/src/modules/home/components/subpages/NavigationContainer';
import { ReportsContextProvider } from '@nile/reports/src/modules/home/contexts/Reports';
import layoutReducer from '@nile/reports/src/modules/reports/utils/layoutReducer';
import ChartToolbar from '@nile/reports/src/modules/charts/components/subpages/ChartToolbar';
import ChartList from '@nile/reports/src/modules/charts/components/pages/ChartList';
import ReportToolbar from '@nile/reports/src/modules/reports/components/subpages/ReportToolbar';
import ReportTvToolbar from '@nile/reports/src/modules/reports/components/subpages/ReportToolbar/ReportTvToolbar';
import ReportCascade from '@nile/reports/src/modules/reports/components/pages/ReportHome/ReportCascade';
import DataReportsHome from '@nile/reports/src/modules/DataReports/components/pages/index';
import EfficiencyReport from '@nile/reports/src/modules/EfficiencyReport/components/pages';
import RetainerReport from '@nile/reports/src/modules/RetinerReport/components/pages';
import InvoiceStatementsReport from '@nile/reports/src/modules/InvoiceStatements/InvoiceStatementsReport';
import { reportObjFormatter } from '@nile/reports/src/modules/reports/helper';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  VIEW_MODE,
  CREATE_MODE,
  EDIT_MODE,
  DEFAULT_VIEW,
  CUSTOM_VIEW,
  DATA_REPORT,
  PDF_REPORT,
  CHARTS,
  REPORTS,
  DATA_REPORT_TYPE,
  EFFICIENCY_REPORT,
  REPORT_PATHNAME,
  METRIC_REPORT,
  PROFITABLITY_REPORT,
  RETAINER_REPORT,
  RETAINER,
  NEWREPORTID,
  INVOICE_STATEMENTS
} from '@nile/reports/src/modules/home/constants';
import { cloneDeep, includes, isEmpty, pick, sortBy } from 'lodash';
import { useIntl } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  StyleContainer,
  StyleNavContainer,
  StylePageContainer
} from './styles';
/**
 *
 * Main Home component for Reporting and Charts
 * @param props
 * @returns JSX
 */

let chartQuery = null;

const Home = (props) => {
  const { match, history } = props;
  const { search } = useLocation();
  const getParams = new URLSearchParams(search);
  const selectedView = getParams.get('selectedView') || null;

  const isNonAuth = !!getNonAuthToken();
  const intl = useIntl();
  const flags = useFlags();

  // Context Variables //
  const tabContext = useContext(TabContext);
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};
  const userRole = appContext?.user?.role?.roleFeatureList || [];
  const isEditAuthorised = isAuthorised(userRole, 'report.edit');
  const isRetainerReportAuthorised = useIsAuthorisedHook(
    'retainerReport.allow'
  );
  const isInvoiceStatementsReportAuthorised = useIsAuthorisedHook(
    'invoiceStatements.allow'
  );
  const user = {
    firstName: currentUser.firstName,
    userId: currentUser.userId,
    email: currentUser.email,
    lastName: currentUser.lastName
  };

  // Layout Variables //
  const [activeView, setActiveView] = useState(
    selectedView || tabContext?.activeTab?.state?.activeView || CUSTOM_VIEW
  );
  const [activeViewLoading, setActiveViewLoading] = useState(false);

  const [loading, setLoading] = useState(false);

  const activeViewRef = useRef(activeView);
  activeViewRef.current = activeView;

  const [activeSection, setActiveSection] = useState({
    section: CHARTS,
    data: {}
  });

  const [reportViewMode, setReportViewMode] = useState(
    tabContext?.activeTab?.state?.reportView || VIEW_MODE
  );
  const reportViewModeRef = useRef(VIEW_MODE);
  reportViewModeRef.current = reportViewMode;

  const [navOpen, setNavOpen] = useState(true);
  const [showChartsLibrary, setShowChartsLibrary] = useState(false);
  const [newReportId, setNewReportId] = useState(null);

  // Reports Variables //

  const [allReports, setAllReports] = useState([]);
  const [activeReport, setActiveReport] = useState({});
  const [activeReportLoading, setActiveReportLoading] = useState(false);
  const [cloneId, setCloneId] = useState(null);

  const [reportCondition, setReportCondition] = useState({
    default:
      (tabContext?.activeTab?.state?.reportGlobalFilter &&
        tabContext?.activeTab?.state?.reportGlobalFilter?.default) ||
      null,
    custom:
      (tabContext?.activeTab?.state?.reportGlobalFilter &&
        tabContext?.activeTab?.state?.reportGlobalFilter?.custom) ||
      null
  });

  const [refreshDataReport, setRefreshDataReport] = useState(false);
  const [refreshWidget, setRefreshWidget] = useState(false);
  const [isTvDashboardEnabled, setIsTvDashboardEnabled] = useState(
    activeReport?.fullScreenModeEnabled
  );
  const [tvRefreshRate, setTvRefreshRate] = useState(
    activeReport?.fullScreenModeRefreshInterval
  );

  const [submitActionLoading, setSubmitActionLoading] = useState(false);
  const [errors, setErrors] = useState(null);

  const reportFilter = useRef({});
  reportFilter.current = reportCondition;

  const initialItems = activeReport?.layout || [];
  const [layout, dispatch] = useReducer(layoutReducer, initialItems);

  // Chart Variables //

  const [chartViews, setChartViews] = useState([]);

  const [chartListMeta, setChartListMeta] = useState({
    refresh: false,
    data: null
  });

  const [chartFilter, setChartFilter] = useState(
    tabContext?.activeTab?.state?.filter || null
  );

  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [selectedChart, setSelectedChart] = useState([]);

  const contextByFilter = useRef({});
  contextByFilter.current = chartFilter;

  const setReportOrder = (reports) => {
    if (!isEmpty(flags.defaultReportOrder) && activeView === DEFAULT_VIEW) {
      const sortedReportedOrder = sortBy(flags.defaultReportOrder, ['order']);
      const sortedReportedOrderId = sortedReportedOrder.map(
        (el) => el.internalName
      );
      const reportsWithOrder = reports.filter((el) =>
        includes(sortedReportedOrderId, el.internalName)
      );
      const reportsWithoutOrder = reports.filter(
        (el) => !includes(sortedReportedOrderId, el.internalName)
      );
      const sortedReportList =
        (reportsWithOrder &&
          reportsWithOrder.sort(
            (a, b) =>
              sortedReportedOrderId.indexOf(a.internalName) -
              sortedReportedOrderId.indexOf(b.internalName)
          )) ||
        [];
      setAllReports([...sortedReportList, ...reportsWithoutOrder]);
      return;
    }
    setAllReports(reports);
  };

  const [getReports] = useLazyQuery(GET_REPORTS, {
    client: AccountClient,
    fetchPolicy: 'cache-and-network',
    onCompleted: (res) => {
      if (res?.getReports) {
        setReportOrder([...res?.getReports.reports]);
      }
      setActiveViewLoading(false);
      setLoading(false);
    },
    onError: () => {
      setActiveViewLoading(false);
    }
  });

  const getAllReports = async (input = null) => {
    const reqVariable = {
      listInfo: {
        sort: [
          {
            attribute: 'name',
            order: 'ASC'
          }
        ],
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'systemReport',
              operator: 'is',
              value: input === DEFAULT_VIEW || activeReport === DEFAULT_VIEW
            }
          ]
        }
      }
    };
    setLoading(true);
    setActiveSection(
      (prevState) => console.log(prevState) || { ...prevState, data: {} }
    );
    dispatch({
      type: 'updateLayout',
      layout: []
    });
    await getReports({ variables: reqVariable });
  };

  const getChartViews = async () => {
    chartQuery = await AccountClient.watchQuery({
      query: GET_CHART_VIEWS,
      fetchPolicy: 'cache-and-network'
    }).subscribe({
      next: ({ data }) => {
        if (data?.getChartViews) {
          setChartViews(data.getChartViews);
        }
      },
      error: () => {}
    });
  };

  // On View mode change, changes the state and layour accordingly
  const changeLayout = () => {
    if (reportViewMode === CREATE_MODE || reportViewMode === EDIT_MODE) {
      setShowChartsLibrary(true);
      setNavOpen(false);
    }
    if (reportViewMode === VIEW_MODE) {
      setShowChartsLibrary(false);
      setNavOpen(true);
    }
  };

  // Mount Functions
  const fetchMetas = (view) => {
    Promise.all([getAllReports(view), getChartViews()]);
  };

  // Retain state on tab switch
  const updateTabState = (eventData) => {
    tabContext.updateTabState(
      {
        reportGlobalFilter: reportFilter.current,
        filter: contextByFilter.current,
        reportView: reportViewModeRef.current,
        activeView: activeViewRef.current
      },
      eventData
    );
  };

  const getReportViewPersonalisation = () => {
    getPersonalization({
      userId: currentUser?.userId,
      category: 'ReportViewPersonalization',
      name: 'ReportView'
    })
      .then((response) => {
        if (response) {
          const res = response?.data?.getPersonalizationByCategoryAndName;
          if (res !== null && !tabContext?.activeTab?.state?.activeView) {
            setActiveView(JSON.parse(res.value));
            return;
          }
          if (tabContext?.activeTab?.state?.activeView) {
            setActiveView(tabContext?.activeTab?.state?.activeView);
          } else {
            setActiveView(DEFAULT_VIEW);
          }
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const updateReportDataContext = async (latestData) => {
    const index = allReports.findIndex(
      (rep) => rep.reportId === latestData.reportId
    );
    const updatedList = cloneDeep(allReports);
    updatedList[index] = latestData;
    setAllReports(updatedList);
    await setActiveReport(reportObjFormatter(latestData));
    setReportViewMode(VIEW_MODE);
  };

  const [createNewReport] = useMutation(CREATE_REPORT, {
    client: AccountClient,
    onCompleted({ createReport }) {
      toast('success', 'reports.dashboard.createDashboard.success');
      setNewReportId({ id: createReport.reportId, type: REPORTS });
      if (activeView === DEFAULT_VIEW) {
        setActiveView(CUSTOM_VIEW);
        const personalisedData = {
          name: 'ReportView',
          category: 'ReportViewPersonalization',
          value: JSON.stringify(CUSTOM_VIEW),
          user: user
        };
        savePersonalization({
          variables: personalisedData,
          input: personalisedData
        });
      }
      getAllReports();
    }
  });

  const [updateReport] = useMutation(UPDATE_REPORT, {
    client: AccountClient,
    onCompleted({ updateReport: updatedReport }) {
      updateReportDataContext(updatedReport);
    }
  });

  const trackReport = (trackerdata) => {
    if (activeView === DEFAULT_VIEW) {
      useActivityTracker({
        event: 'DEFAULT_REPORT_CLONE',
        data: trackerdata
      });
    }
  };

  const handleReportSave = async (hideToast = false) => {
    const primaryAction =
      reportViewMode === CREATE_MODE ? createNewReport : updateReport;
    let updatedReportData = cloneDeep(activeReport);
    const validationResult = validateForm(
      preFormatForm({ name: updatedReportData.name }),
      ReportConstraints
    );
    if (validationResult) {
      setErrors(validationResult);
      return;
    }
    setSubmitActionLoading(true);
    setErrors(null);

    const { name, internalName } = updatedReportData;
    trackReport({
      reportId: cloneId,
      name: name,
      internalName: internalName
    });

    const chartInLayout = layout.map((el) => el.chart.chartId);

    const charts = updatedReportData.charts.filter((el) =>
      chartInLayout.includes(el.chartId)
    );

    const { addedItems, deletedItems } = getParsedCrudData(
      updatedReportData.parentCharts,
      charts,
      'chartId'
    );
    updatedReportData.addCharts = addedItems.map(({ chartId }) => ({
      chartId
    }));
    updatedReportData.deleteCharts = deletedItems.map(({ chartId }) => ({
      chartId
    }));
    // get layout from reducer
    updatedReportData.layout = { layout: layout };
    updatedReportData.notificationTemplate = pick(
      updatedReportData.notificationTemplate,
      ['content', 'placeholdersUsed', 'subject']
    );
    updatedReportData = pick(updatedReportData, [
      'reportId',
      'name',
      'description',
      'layout',
      'addCharts',
      'deleteCharts'
    ]);
    if (activeReport?.reportId) {
      primaryAction({
        variables: {
          reportId: activeReport.reportId,
          input: updatedReportData
        }
      })
        .then((res) => {
          if (!hideToast) {
            toast('success', 'reports.dashboard.updateDashboard.success');
          }
        })
        .finally(() => setSubmitActionLoading(false));
    } else {
      primaryAction({ variables: { input: updatedReportData } })
        .then((res) => {
          if (res.data.createReport) {
            if (cloneId) setCloneId(null);
          }
        })
        .finally(() => {
          setSubmitActionLoading(false);
        });
    }
  };

  useEffect(() => {
    changeLayout();
  }, [reportViewMode]);

  /**
   * Effect to get all reports and charts initially
   */
  useEffect(() => {
    if (!isNonAuth) {
      fetchMetas();
    }
    if (!selectedView) {
      getReportViewPersonalisation();
    }
    EventEmitter.subscribe('tabChange', updateTabState);
    return function cleanup() {
      if (chartQuery) chartQuery.unsubscribe();
      EventEmitter.unsubscribe('tabChange');
    };
  }, []);

  useEffect(() => {
    if (tabContext?.activeTab?.state?.activeView)
      setActiveView(tabContext?.activeTab?.state?.activeView);
  }, [tabContext?.activeTab?.state?.activeView]);
  /**
   * Reset layout object when switched to chart view
   */
  useEffect(() => {
    dispatch({
      type: 'updateLayout',
      layout: []
    });
  }, [activeSection?.section, activeView]);

  /**
   * Effect to change default and custom view
   */

  useEffect(() => {
    if (!isNonAuth) {
      // get all reports query for auth user
      setActiveViewLoading(true);
      setAllReports([]);
      setActiveSection((prevState) => ({ ...prevState, data: {} }));
      getAllReports(activeView);
    }
  }, [activeView]);
  /**
   * Effect to redirect to respective page based on URL
   */
  useEffect(() => {
    if (match.params.viewtype === REPORTS) {
      if (match.params.id === NEWREPORTID && !newReportId) {
        if (reportViewMode !== CREATE_MODE) setReportViewMode(CREATE_MODE);
        if (cloneId) {
          const clonedReport = reportObjFormatter({
            name: `${activeReport?.name} - ${getTranslatedText(
              intl,
              'common.clone'
            )}`,
            charts: activeReport?.charts,
            layout: { layout: activeReport?.layout },
            internalName: activeReport.internalName,
            parentCharts: []
          });
          setActiveReport(clonedReport);
          setActiveSection({
            section: REPORTS,
            data: clonedReport
          });
          return;
        }
        setActiveSection({
          section: REPORTS,
          data: reportObjFormatter({})
        });
        setActiveReport(reportObjFormatter({}));
        return;
      }
      if (allReports?.length) {
        if (reportViewMode !== VIEW_MODE) setReportViewMode(VIEW_MODE);
        if (newReportId) {
          setNewReportId(null);
          history.push(`/${newReportId.type}/${newReportId.id}/view`);
          return;
        }

        const metricReports = [METRIC_REPORT];

        if (isRetainerReportAuthorised) {
          metricReports.push(RETAINER);
        }

        if (isInvoiceStatementsReportAuthorised) {
          metricReports.push(INVOICE_STATEMENTS);
        }

        const metricreport = allReports.filter(
          (el) =>
            el.reportType === null || metricReports.includes(el.reportType)
        );
        const activeReports = metricreport?.find(
          (rep) => rep.reportId === match.params.id
        );

        if (!isEmpty(metricreport)) {
          if (activeReports) {
            setActiveReport(reportObjFormatter(activeReports));
            setActiveSection({
              section: REPORTS,
              data: reportObjFormatter(activeReports)
            });
          } else {
            history.push(
              `/${REPORT_PATHNAME[metricreport?.[0]?.reportType] || 'report'}/${
                metricreport?.[0]?.reportId
              }/view`
            );
          }
        } else {
          history.push(
            `/${REPORT_PATHNAME[allReports?.[0]?.reportType] || 'report'}/${
              allReports?.[0]?.reportId
            }/view`
          );
        }
      } else {
        setActiveReport(reportObjFormatter({}));
        setActiveSection({
          section: REPORTS,
          data: reportObjFormatter({})
        });
      }
    }
    if (match.params.viewtype === CHARTS && !isEmpty(chartViews)) {
      if (activeView === DEFAULT_VIEW && match.params.id !== '2001') {
        history.push(`/charts/2001/All Widgets`);
        return;
      }
      setActiveSection({
        section: CHARTS,
        data: match.params.id === '2001' ? chartViews[0] : chartViews[1]
      });
    }
    /* PDF view */
    if (match.params.viewtype === 'pdfreport') {
      if (allReports.length) {
        const activeReports = allReports?.find(
          (rep) => rep.reportId === match.params.id
        );

        if (activeReports && activeView === DEFAULT_VIEW) {
          setActiveReport(reportObjFormatter(activeReports));
          setActiveSection({
            section: PDF_REPORT,
            data: reportObjFormatter(activeReports)
          });
        } else {
          history.push(`/report/${allReports?.[0]?.reportId}/view`);
        }
      }
    }
    if (match.params.viewtype === 'efficiency') {
      if (!isEmpty(allReports)) {
        const effReport = allReports.filter(
          (el) => el.reportType === EFFICIENCY_REPORT
        );
        const activeReports = allReports?.find(
          (rep) => rep.reportId === match.params.id
        );
        if (!isEmpty(effReport)) {
          if (activeReports && activeView === DEFAULT_VIEW) {
            setActiveReport(activeReports);
            setActiveSection({
              section: EFFICIENCY_REPORT,
              data: activeReports
            });
          } else {
            history.push(`/efficiency/${effReport?.[0]?.reportId}/view`);
          }
        } else {
          history.push(
            `/${REPORT_PATHNAME[allReports?.[0]?.reportType] || 'report'}/${
              allReports?.[0]?.reportId
            }/view`
          );
        }
      }
    }
    if (match.params.viewtype === REPORT_PATHNAME.PROFITABILITY) {
      if (!isEmpty(allReports)) {
        const allreport = allReports || [];
        const activeReports = allreport?.find(
          (rep) => rep.reportId === match.params.id
        );
        if (activeReports && activeView === DEFAULT_VIEW) {
          setActiveReport(activeReports);
          setActiveSection({
            section: PROFITABLITY_REPORT,
            data: activeReports
          });
        } else {
          history.push(`/report/${allreport?.[0]?.reportId}/view`);
        }
      }
    }
    if (match.params.viewtype === DATA_REPORT) {
      if (allReports.length) {
        if (newReportId) {
          setNewReportId(null);
          history.push(`/${newReportId?.type}/${newReportId?.id}/view`);
          return;
        }
        const dataReports = allReports.filter(
          (el) => el.reportType === DATA_REPORT_TYPE
        );
        const activeReports = dataReports?.find(
          (rep) => rep.reportId === match.params.id
        );
        if (activeReports) {
          setActiveReport(activeReports);
          setActiveSection({
            section: DATA_REPORT,
            data: activeReports
          });
        } else {
          if (!isEmpty(dataReports)) {
            history.push(`/datareport/${dataReports?.[0]?.reportId}/view`);
            return;
          }
          history.push(`/report/${allReports?.[0]?.reportId}/view`);
        }
      }
    }
  }, [match.params, allReports, chartViews]);

  useEffect(() => {
    if (activeReport?.fullScreenModeEnabled) {
      setNavOpen(false);
    }
    setIsTvDashboardEnabled(!!activeReport?.fullScreenModeEnabled);
  }, [activeReport?.fullScreenModeEnabled]);

  useEffect(() => {
    if (activeReport?.fullScreenModeRefreshInterval) {
      setTvRefreshRate(activeReport?.fullScreenModeRefreshInterval);
    }
  }, [activeReport?.fullScreenModeRefreshInterval]);

  const providerValue = {
    navOpen,
    setNavOpen,
    activeSection,
    setActiveSection,
    refreshDataReport,
    setRefreshDataReport,
    refreshWidget,
    setRefreshWidget,
    isTvDashboardEnabled,
    setIsTvDashboardEnabled,
    isTvDashboard: props?.isTvDashboard,
    tvRefreshRate,
    setTvRefreshRate,
    isReportEditAuthorised: isEditAuthorised,
    getAllReports,
    allReports,
    setAllReports,
    reportCondition,
    setReportCondition,
    chartFilter,
    setChartFilter,
    setChartListMeta,
    reportViewMode,
    setReportViewMode,
    showChartsLibrary,
    handleReportSave,
    activeReport,
    setActiveReport,
    updateReportDataContext,
    layout,
    dispatch,
    cloneId,
    setCloneId,
    showBulkMenu,
    setShowBulkMenu,
    setNewReportId,
    selectedChart,
    setSelectedChart,
    reportFilter,
    activeView,
    setActiveView
  };

  if (loading)
    return props?.isTvDashboard ? (
      <div className="flex-center full-height">
        <Loading className="w90" />
      </div>
    ) : (
      <SkeletonLoading
        hideSecondaryHeader
        hideHeaderSub
        removeTableMargin
        hideThumbnail
        headerSecondaryActionCount={1}
      />
    );

  const getToolbarContent = () => {
    switch (activeSection.section) {
      case DATA_REPORT:
      case PDF_REPORT:
      case EFFICIENCY_REPORT:
      case PROFITABLITY_REPORT:
      case REPORTS:
        return props?.isTvDashboard ? (
          <ReportTvToolbar
            reportViewMode={reportViewMode}
            handleSave={handleReportSave}
            submitActionLoading={submitActionLoading}
            errors={errors}
            reportType={activeSection.section}
            activeViewName={activeView}
            isNonAuth={isNonAuth}
            reportInternalName={activeSection?.data?.reportType}
          />
        ) : (
          <ReportToolbar
            reportViewMode={reportViewMode}
            handleSave={handleReportSave}
            submitActionLoading={submitActionLoading}
            errors={errors}
            reportType={activeSection.section}
            activeViewName={activeView}
            reportInternalType={activeSection?.data?.reportType}
          />
        );
      case CHARTS:
      default:
        return <ChartToolbar />;
    }
  };

  const renderRightPane = () => {
    // 1 is default report id from tab manager so to avoid the getquery for default report id because it is not required
    const reportId =
      isNonAuth && match?.params?.id && match.params.id !== '1'
        ? match.params.id
        : null;
    switch (activeSection.section) {
      case PDF_REPORT:
      case REPORTS:
        if (activeSection?.data?.reportType === RETAINER) {
          return (
            <>
              {activeViewLoading ? (
                <div className="flex-center full-height">
                  <Loading className="w90" />
                </div>
              ) : (
                <RetainerReport
                  data={activeSection.data}
                  setActiveReport={setActiveReport}
                  setActiveSection={setActiveSection}
                  setActiveReportLoading={setActiveReportLoading}
                />
              )}
            </>
          );
        }
        if (activeSection?.data?.reportType === INVOICE_STATEMENTS) {
          return (
            <>
              {activeViewLoading ? (
                <div className="flex-center full-height">
                  <Loading className="w90" />
                </div>
              ) : (
                <InvoiceStatementsReport
                  data={activeSection.data}
                  setActiveReport={setActiveReport}
                  setActiveSection={setActiveSection}
                  setActiveReportLoading={setActiveReportLoading}
                />
              )}
            </>
          );
        }
        return (
          <>
            {activeViewLoading ? (
              <div className="flex-center full-height">
                <Loading className="w90" />
              </div>
            ) : (
              <ReportCascade
                data={activeSection.data}
                setActiveReport={setActiveReport}
                setActiveSection={setActiveSection}
                setActiveReportLoading={setActiveReportLoading}
                reportType={activeSection.section}
                // reportId, AccountClient, isNonAuth are for non auth user
                AccountClient={isNonAuth ? NonAuthAccountClient : AccountClient}
                isNonAuth={isNonAuth}
                reportId={activeSection.data.reportId || reportId}
              />
            )}
          </>
        );
      case EFFICIENCY_REPORT:
      case PROFITABLITY_REPORT:
        return (
          <>
            {activeViewLoading ? (
              <div className="flex-center full-height">
                <Loading className="w90" />
              </div>
            ) : (
              <EfficiencyReport
                data={activeSection.data}
                setActiveReport={setActiveReport}
                setActiveSection={setActiveSection}
                setActiveReportLoading={setActiveReportLoading}
                reportType={activeSection.section}
              />
            )}
          </>
        );
      case DATA_REPORT:
        return (
          <>
            {activeViewLoading ? (
              <div className="flex-center full-height">
                <Loading className="w90" />
              </div>
            ) : (
              <DataReportsHome
                data={activeSection.data}
                setActiveReport={setActiveReport}
                setActiveSection={setActiveSection}
                activeViewLoading={activeViewLoading}
                setActiveReportLoading={setActiveReportLoading}
                reportType={activeSection.section}
              />
            )}
          </>
        );
      case CHARTS:
        return (
          <>
            <ChartList
              activeView={activeSection.data}
              meta={chartListMeta}
              activeViewLoading={activeViewLoading}
              viewType={activeView}
            />
          </>
        );
      default:
        return <></>;
    }
  };

  return (
    <div className="fx col full-height">
      <ReportsContextProvider value={providerValue}>
        {!props.isTvDashboard ? (
          <>
            {getToolbarContent()}
            <StyleContainer className="fx">
              <Sheet
                sheet={
                  <StyleNavContainer className="p8 w300">
                    <NavigationContainer
                      reports={allReports}
                      chartViews={chartViews}
                      setActiveSection={setActiveSection}
                      setReportViewMode={setReportViewMode}
                      setActiveReport={setActiveReport}
                      activeReportLoading={activeReportLoading}
                      activeView={activeView}
                      setActiveView={setActiveView}
                      activeViewLoading={activeViewLoading}
                      loading={loading}
                    />
                  </StyleNavContainer>
                }
                docked={navOpen}
                styles={{
                  sheet: {
                    overflowY: 'hidden',
                    backgroundColor: 'var(--sops-solid-purple-to-black)'
                  }
                }}
                open={navOpen}
                transitions
              />
              <StylePageContainer
                navOpen={navOpen}
                className={`sops-pos-relative px10 ${
                  activeSection.section === CHARTS ? 'bg-white' : ''
                }`}
              >
                {renderRightPane()}
              </StylePageContainer>
            </StyleContainer>
          </>
        ) : (
          <>
            {getToolbarContent()}
            {renderRightPane()}
          </>
        )}
      </ReportsContextProvider>
    </div>
  );
};

export default withRouter(withErrorBoundary(Home));
